import Rolling from '@assets/gif/rolling.gif';

import { format } from 'date-fns';

import ReactTooltip from 'react-tooltip';

import AddUserIcon from '@assets/svg/add-user.svg';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { CompletedMenu } from '@components/UpdatesPageMenus/CompletedMenu/CompletedMenu';
import Update, { IUpdateRawQueryList } from '@models/Update.model';
import {
  UpdateStatusPriority,
  UpdateStatusType,
} from '@models/UpdateStatus.model';
import {
  LoadingImg,
  LoadingText,
  LoadMore,
} from '@pages/Products/components/ListProducts/ListProducts.styles';
import { UpdatesContext } from '@pages/Updates/Updates';
import { updateSort, updateSortDirection } from '@store/redux/features/updates';
import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { getUpdateFlagColor } from '@utils/index';
import { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { ColumnTitle } from './ColumnTitle';
import { LoadingLine } from './components/LoadingLine/LoadingLine';
import { TruncatedText } from './components/TruncatedText/TruncatedText';
import { TableContentLoading } from './Updates.loading';
import {
  AffectsContiner,
  AffectsNumber,
  Assignee,
  Container,
  Content,
  FlagContainer,
  HeadingPrioritized,
  Internal,
  LineInnerPrioritized,
  LineItem,
  LineMenu,
  LinePrioritized,
  LineText,
} from './Updates.styles';
import { SortUpdatesCompletedTypes } from './UpdatesCompleted.query';
import { getUpdate } from './getUpdate';

interface IUpdatesCompletedRaw {
  updateStatuses: IUpdateRawQueryList[];
  loadMore: () => void;
  hasMore?: boolean;
}

const UpdatesCompletedRaw = ({
  updateStatuses,
  loadMore,
  hasMore,
}: IUpdatesCompletedRaw) => {
  const { updates, updatesOptimistic } = useAppSelector();
  const dispatch = useAppDispatch();
  const { ref, inView } = useInView();
  const { updateStatusId } = useContext(UpdatesContext);

  useEffect(() => {
    if (inView && hasMore) loadMore();
  }, [inView, hasMore]);

  const clickColumn = (column: SortUpdatesCompletedTypes) => {
    if (updates.options.sort === column) {
      if (updates.options.direction === 'ASC') {
        dispatch(updateSortDirection('DESC'));
        return;
      }
      if (updates.options.direction === 'DESC') {
        dispatch(updateSortDirection(undefined));
        return;
      }
      dispatch(updateSortDirection('ASC'));
    } else {
      dispatch(updateSortDirection('ASC'));
    }
    dispatch(updateSort(column));
  };

  const directionResult = (
    column: SortUpdatesCompletedTypes,
  ): 'up' | 'down' | undefined => {
    if (updates.options.sort === column) {
      if (updates.options.direction === 'ASC') {
        return 'up';
      }
      if (updates.options.direction === 'DESC') {
        return 'down';
      }
    }
    return undefined;
  };

  const UpdateFlags = ({ priority }: IUpdateRawQueryList) => {
    return (
      <FlagContainer>
        <BlueIcon icon="flag" color={getUpdateFlagColor(priority)} />
      </FlagContainer>
    );
  };

  const ToolTipsText = ({ priority }: IUpdateRawQueryList) => {
    if (priority === UpdateStatusPriority.URGENT) return 'Urgent';
    if (priority === UpdateStatusPriority.IMPORTANT) return 'Important';
    if (priority === UpdateStatusPriority.NONCRITICAL) return 'Non-critical';
    return 'Uncertain';
  };

  return (
    <Container>
      <HeadingPrioritized>
        <ColumnTitle
          label="Priority"
          onClick={() => clickColumn('priority')}
          direction={directionResult('priority')}
        />
        <ColumnTitle
          label="Subject"
          onClick={() => clickColumn('subject')}
          direction={directionResult('subject')}
        />
        <ColumnTitle
          label="Affects"
          onClick={() => clickColumn('affects')}
          direction={directionResult('affects')}
        />
        <ColumnTitle
          label="Assignee"
          onClick={() => clickColumn('assignee')}
          direction={directionResult('assignee')}
        />
        <ColumnTitle
          label="Due Date"
          onClick={() => clickColumn('dueDate')}
          direction={directionResult('dueDate')}
        />
        <ColumnTitle
          label="From"
          onClick={() => clickColumn('pManufacturers')}
          direction={directionResult('pManufacturers')}
        />
        <div />
      </HeadingPrioritized>

      {updateStatuses ? (
        <>
          <Content>
            {updatesOptimistic.backupNewUpdates
              .map(item => item.update)
              .filter(item => item.usType === UpdateStatusType.COMPLETED)
              .map(update => {
                if (
                  updatesOptimistic.deletedUpdates?.find(
                    item => item.updateId === update.id,
                  ) ||
                  updateStatuses.find(item => item.id === update.id)
                ) {
                  return null;
                }

                const updateStatus = Update.fromRawQueryListApiModel(update);

                return (
                  <LinePrioritized
                    key={updateStatus.id}
                    onClick={() => {
                      if (!updates.loadingUpdateId.includes(updateStatus.id)) {
                        updateStatusId.set(updateStatus.id);
                      }
                    }}
                  >
                    {updates.loadingUpdateId.includes(updateStatus.id) && (
                      <LoadingLine />
                    )}
                    <LineInnerPrioritized>
                      <LineItem
                        data-tip
                        data-for={`helpTooltip ${updateStatus.id}`}
                      >
                        {UpdateFlags(updateStatus)}
                        <ReactTooltip
                          className="tooltip"
                          id={`helpTooltip ${updateStatus.id}`}
                          effect="solid"
                          place="bottom"
                          multiline
                        >
                          {ToolTipsText(updateStatus)}
                        </ReactTooltip>
                      </LineItem>
                      <LineItem>
                        <TruncatedText>{updateStatus.subject}</TruncatedText>
                      </LineItem>
                      <LineItem>
                        <LineText limitOfLines={1}>
                          <AffectsContiner>
                            <BlueIcon icon="cube" color="#1371FE" size={12} />
                            <AffectsNumber>
                              {updateStatus.affects}
                            </AffectsNumber>
                          </AffectsContiner>
                        </LineText>
                      </LineItem>
                      <LineItem>
                        {updateStatus.aAvatar ? (
                          <Assignee src={updateStatus.aAvatar} />
                        ) : (
                          <img src={AddUserIcon} alt="add assignee" />
                        )}
                      </LineItem>
                      <LineItem>
                        <LineText limitOfLines={1}>
                          {updateStatus.dueDate &&
                            format(updateStatus.dueDate, 'MMM do yyyy')}
                        </LineText>
                      </LineItem>
                      <LineItem>
                        <LineText limitOfLines={1}>
                          {(() => {
                            if (updateStatus.usStudioId) {
                              return <Internal>INTERNAL</Internal>;
                            }

                            if (updateStatus.type === 'products') {
                              return (
                                <TruncatedText>
                                  {updateStatus.pManufacturer}
                                </TruncatedText>
                              );
                            }

                            return '';
                          })()}
                        </LineText>
                      </LineItem>
                    </LineInnerPrioritized>
                    <LineMenu onClick={e => e.stopPropagation()}>
                      <CompletedMenu updateStatus={updateStatus} />
                    </LineMenu>
                  </LinePrioritized>
                );
              })}
            {updateStatuses.map(update => {
              if (
                updatesOptimistic.deletedUpdates?.find(
                  item => item.updateId === update.id,
                )
              ) {
                return null;
              }

              const updateStatus = getUpdate(
                update,
                updatesOptimistic.backupUpdatedUpdates,
              );

              return (
                <LinePrioritized
                  key={updateStatus.id}
                  onClick={() => {
                    if (!updates.loadingUpdateId.includes(updateStatus.id)) {
                      updateStatusId.set(updateStatus.id);
                    }
                  }}
                >
                  {updates.loadingUpdateId.includes(updateStatus.id) && (
                    <LoadingLine />
                  )}
                  <LineInnerPrioritized>
                    <LineItem
                      data-tip
                      data-for={`helpTooltip ${updateStatus.id}`}
                    >
                      {UpdateFlags(updateStatus)}
                      <ReactTooltip
                        className="tooltip"
                        id={`helpTooltip ${updateStatus.id}`}
                        effect="solid"
                        place="bottom"
                        multiline
                      >
                        {ToolTipsText(updateStatus)}
                      </ReactTooltip>
                    </LineItem>
                    <LineItem>
                      <TruncatedText>{updateStatus.subject}</TruncatedText>
                    </LineItem>
                    <LineItem>
                      <LineText limitOfLines={1}>
                        <AffectsContiner>
                          <BlueIcon icon="cube" color="#1371FE" size={12} />
                          <AffectsNumber>{updateStatus.affects}</AffectsNumber>
                        </AffectsContiner>
                      </LineText>
                    </LineItem>
                    <LineItem>
                      {updateStatus.aAvatar ? (
                        <Assignee src={updateStatus.aAvatar} />
                      ) : (
                        <img src={AddUserIcon} alt="add assignee" />
                      )}
                    </LineItem>
                    <LineItem>
                      <LineText limitOfLines={1}>
                        {updateStatus.dueDate &&
                          format(updateStatus.dueDate, 'MMM do yyyy')}
                      </LineText>
                    </LineItem>
                    <LineItem>
                      <LineText limitOfLines={1}>
                        {(() => {
                          if (updateStatus.usStudioId) {
                            return <Internal>INTERNAL</Internal>;
                          }

                          if (updateStatus.type === 'products') {
                            return (
                              <TruncatedText>
                                {updateStatus.pManufacturer}
                              </TruncatedText>
                            );
                          }

                          return '';
                        })()}
                      </LineText>
                    </LineItem>
                  </LineInnerPrioritized>
                  <LineMenu onClick={e => e.stopPropagation()}>
                    <CompletedMenu updateStatus={updateStatus} />
                  </LineMenu>
                </LinePrioritized>
              );
            })}
          </Content>
          <LoadMore ref={ref}>
            {hasMore ? (
              <LoadMore>
                <LoadingText>Loading more</LoadingText>
                <LoadingImg src={Rolling} alt="loading" />
              </LoadMore>
            ) : (
              <div />
            )}
          </LoadMore>
        </>
      ) : (
        <TableContentLoading />
      )}
    </Container>
  );
};

export { UpdatesCompletedRaw };
